import { Card, Grid, Typography, Button, FormControlLabel, Checkbox, TextField, Box, Alert, AlertTitle, Divider, Tooltip } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react'
import { Field, FormikProvider, useFormik } from 'formik';

import * as Yup from "yup";
import api from '../../../redux/features/auth/axiosAuthHelper';
import dayjs from 'dayjs';
import { TextEditField, TextEditReadOnlyField, FormDatePicker, TestTypeSelectField, StripTextEditField, SelectEditField, StripSelectEditField, TwolineStripTextEditField, StripSelectReadOnlyEditField, StripTextReadOnlyEditField, TwolineStripReadOnlyTextEditField } from '../fields/fields';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import colorConfigs from '../../../configs/colorConfigs';
import { SkuSessionStorageKey, SkuTableParameterStorageService } from '../../../services/sessionStorage';
import RareButton from '../buttons/RareButton';
import { Sku, SkuStrip, NewSkuStrip } from "../types";
import { drugCodes } from '../CustomHooks';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

interface SkuDetailProps {
    sku_id?: any | null;
    handleRefreshButton: () => void;
    handleBackButton: () => void;
}


const SkuDetailCard: React.FC<SkuDetailProps> = ({ sku_id, handleRefreshButton, handleBackButton }) => {
    const authData = useSelector((state: RootState) => state.auth);
    const [errors, setErrors] = useState<string | null>(null);
    const [initialValues, setInitialValues] = useState<any>({})
    const [updatedValues, setUpdatedValues] = useState<any>({})
    const [hasMadeChanges, setHasMadeChanges] = useState<boolean>(false)
    const [sku, setSku] = useState<Sku>();
    const [activeStrips, setActiveStrips] = useState<string[]>([]);

    const create = !sku_id;

    // Fetch sku data and point_model JSON data
    useEffect(() => {
        if (sku_id) {
            const fetchSku = async () => {
                try {
                    const url = `skus/${sku_id}`;

                    const response = await api.get(url, {
                        headers: {
                            'Authorization': `Bearer ${authData.access}`
                        }
                    });
                    setSku(response.data as Sku);
                } catch (error) {
                    console.error('Error fetching sku', error);
                    setErrors(`No Sku found with ID ${sku_id}`)
                    setTimeout(() => {
                        handleBackButton()
                    }, 4000);
                }
            };
            fetchSku();
        }
    }, [sku_id]);

    // Helper function: if sku data doesn’t include 10 strips, create default placeholders.
    // Update the return type to include a dragId property.
    const generateDefaultStrips = (existingStrips?: SkuStrip[]): (SkuStrip & { dragId: string })[] => {
        return new Array(10).fill(null).map((_, idx) => {
            const found = existingStrips?.find((s) => s.position === idx);
            if (found) {
                return { ...found, dragId: found.id ? `strip-drag-id-${idx + 1}/10` : `strip-drag-id-${idx + 1}/10` };
            }
            return {
                id: 0,
                sku: 0,
                position: idx,
                label: '',
                threshold: 0,
                invalid_threshold: 0,
                dragId: `strip-drag-id-${idx + 1}/10`,
            };
        });
    };

    const extractErrorMessages = (data: any): string => {
        if (typeof data === 'string') {
            return data;
        }
        if (Array.isArray(data)) {
            return data.map(item => extractErrorMessages(item)).join(', ');
        }
        if (typeof data === 'object' && data !== null) {
            return Object.keys(data)
                .map(key => `${key}: ${extractErrorMessages(data[key])}`)
                .join('; ');
        }
        return String(data);
    };

    // Define validation schema for sku
    const validationSchema = Yup.object().shape({
        code: Yup.string().required('Code is required'),
        is_active: Yup.boolean().required('Status is required'),
        strips: Yup.array().of(
            Yup.object().shape({
                position: Yup.number().required(),
                label: Yup.string(),
                threshold: Yup.number().nullable().min(0, 'Value must be between 0 and 1.').max(1, 'Value must be between 0 and 1.'),
                invalid_threshold: Yup.number().nullable(),
            })
        )    // First test: there must be at least one non-empty label in the strips array.
            .test('min-one', 'At least one strip is required.', function (strips) {
                if (!strips) return false;
                const validStrips = strips
                    .map(strip => (strip.label || '').trim())
                    .filter(label => label !== '');
                return validStrips.length >= 1;
            })
            // Second test: each non-empty strip label must be unique.
            .test('unique-labels', 'Strip labels must be unique.', function (strips) {
                if (!strips) return true;
                const labels = strips
                    .map(strip => (strip.label || '').trim())
                    .filter(label => label !== '');
                const uniqueLabels = new Set(labels);
                return uniqueLabels.size === labels.length;
            }),
    });

    // Use a memoized version to avoid regenerating dragIds on each render.
    const initialStrips = useMemo(() => {
        return sku ? generateDefaultStrips(sku.strips) : generateDefaultStrips();
    }, [sku]);


    const formik = useFormik({
        enableReinitialize: true,
        validateOnChange: false,
        validateOnBlur: false,
        initialValues: {
            id: sku?.id || '',
            code: sku?.code || '',
            is_active: sku?.is_active ?? true,
            strips: initialStrips,
        },
        validationSchema,
        onSubmit: async (values) => {
            // Filter out any empty strip (assuming an empty label means the strip should be omitted).
            const submissionStrips = values.strips
                .filter((strip: SkuStrip & { dragId: string }) => strip.label.trim() !== '')
                .map((strip: SkuStrip & { dragId: string }) => ({
                    label: strip.label,
                    position: strip.position,
                    threshold: strip.threshold,
                    invalid_threshold: strip.invalid_threshold,
                }));

            // Build the data object for submission (only including code and the filtered strips)
            const data = { code: values.code, is_active: values.is_active, strips: submissionStrips };

            // Define URL and method for patch/post.
            const url = sku ? `skus/${sku.id}/` : `skus/`;
            const method = sku ? 'patch' : 'post';

            try {
                console.log("data", data)
                await api({
                    method,
                    url,
                    data,
                    headers: {
                        'Authorization': `Bearer ${authData.access}`,
                    },
                });
                setErrors(null);
                console.log("Sku successfully updated")
                handleRefreshButton() // reset states and close popup
                const count = SkuTableParameterStorageService.get(SkuSessionStorageKey.count)
                SkuTableParameterStorageService.set(SkuSessionStorageKey.count, count + 1) // Updated count to not trigger refresh data button
            } catch (error: any) {
                if (error?.response?.data) {
                    const errorMessages = extractErrorMessages(error.response.data);
                    setErrors(errorMessages);
                } else {
                    setErrors("An unknown error occurred");
                }
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },
    });


    // Effect to set initial form values from sku to check for form changes
    useEffect(() => {
        setTimeout(() => {
            setInitialValues(formik.initialValues);
            setUpdatedValues(formik.initialValues);
        }, 500);
        setHasMadeChanges(false);
    }, [sku]);

    // Update the updatedValues state when formik values change
    useEffect(() => {
        setUpdatedValues(formik.values);
    }, [formik.values]);

    // Function to check if form has changed so the save button becomes active on edit
    const hasFormChanged = (flatModel: Record<string, any>, formValues: Record<string, any>): boolean => {
        for (const key in formValues) {
            if (key === "expiry_date") {
                const formDate = dayjs(formValues[key]).format('YYYY-MM-DD');
                const initialDate = create ? dayjs(flatModel[key]).format('YYYY-MM-DD') : dayjs(formValues[key]).format('YYYY-MM-DD');
                if (formDate !== initialDate) {
                    console.log("Changed Date", formDate, initialDate)
                    return true;
                }
            } else {
                if (formValues[key] != flatModel[key]) {
                    console.log("Changed Data", formValues[key], flatModel[key])
                    return true;
                }
            }
        }
        return false;
    };

    // Use Effect to check if any changes ahve been made to the form on Edit
    useEffect(() => {
        if (!create && sku) {
            if (Object.keys(initialValues).length > 0 && Object.keys(updatedValues).length > 0) {
                const formChanged = hasFormChanged(initialValues, updatedValues);
                setHasMadeChanges(formChanged);
            }
        }
    }, [updatedValues, initialValues, sku]);

    // Use Effect set save button active on load if Create and if changes are made on Edit
    useEffect(() => {
        if (create) {
            setHasMadeChanges(true);
        } else {
            setHasMadeChanges(false);
        }
    }, [create]);

    // Handle drag and drop reordering
    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        const newStrips = Array.from(formik.values.strips);
        const [removed] = newStrips.splice(result.source.index, 1);
        newStrips.splice(result.destination.index, 0, removed);
        newStrips.forEach((strip, index) => {
            strip.position = index; // adjust if you want 1-based: index + 1
        });
        formik.setFieldValue("strips", newStrips);
    };

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} style={{ overflow: 'auto' }}>
                <Box data-testid="Box-5899" sx={{ height: "100%", p: 2, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    {errors && (
                        <Alert data-testid="Alert-t5wb" severity="error" style={{ width: "59.5%" }}>
                            <AlertTitle data-testid="AlertTitle-288k">Error</AlertTitle>
                            {errors}
                        </Alert>
                    )}
                    {formik.errors.strips && typeof formik.errors.strips === 'string' && (
                        <Alert data-testid="Alert-fu4i" severity="error" style={{ width: "59.5%" }}>
                            <AlertTitle data-testid="AlertTitle-ur9k">Error</AlertTitle>
                            {formik.errors.strips}
                        </Alert>
                    )}
                    <div data-testid="div-v34x" style={{ width: "100%", height: "100%", margin: "auto", padding: "1em", display: "flex", alignItems: "center" }}>
                        <Grid container spacing={5} alignItems="top" justifyContent="center" style={{ minWidth: "100%", padding: 5 }}>
                            <Grid item xs={12} md={5} style={{ display: "flex", flexDirection: "column", }}>
                                <Card data-testid="Card-4se5" sx={{ pb: 1, boxShadow: 10, height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box data-testid="Box-8mia" sx={{ display: 'flex' }}>
                                        <Box data-testid="Box-8bo2" sx={{ height: '100%', background: colorConfigs.tables.headBg, display: 'flex', alignItems: 'center', borderBottomRightRadius: 3 }}>
                                            <Typography data-testid="Typography-s5t9" sx={{ px: 2, py: 0.5, color: '#fff', fontWeight: 'bold', fontSize: '20px' }}>SKU Details</Typography>
                                        </Box>
                                    </Box>
                                    <Box data-testid="Box-in5w" sx={{ p: 2, pt: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        {/* {!create && <Field data-testid="Field-pkqg" name="id" disabled type="text" className="form-control" label="Sku ID" component={TextEditReadOnlyField} />} */}
                                        <Field data-testid="Field-9awx" name="code" type="text" className="form-control" label="Sku Code" component={create ? TextEditField : TextEditReadOnlyField} />

                                        <FormControlLabel
                                            sx={{ mt: 0.5 }}
                                            control={
                                                <Checkbox
                                                    checked={formik.values.is_active}
                                                    name="is_active"
                                                    sx={{
                                                        '&.Mui-checked': {
                                                            color: colorConfigs.buttons.active,
                                                        },
                                                    }}
                                                    disabled={!authData.isPortalUser}
                                                    onChange={formik.handleChange}
                                                    className="form-control"
                                                />
                                            }
                                            label={
                                                <Box data-testid="Box-y4wb" display="flex" alignItems="center">
                                                    SKU is
                                                    <Typography data-testid="Typography-u72y"
                                                        fontSize="smaller"
                                                        sx={{
                                                            paddingX: 0.7,
                                                            border: formik.values.is_active
                                                                ? `2px solid ${colorConfigs.buttons.active}`
                                                                : `1px solid ${colorConfigs.buttons.inactive}`,
                                                            borderRadius: 2,
                                                            color: formik.values.is_active
                                                                ? colorConfigs.buttons.active
                                                                : colorConfigs.buttons.inactive,
                                                            marginLeft: 1,
                                                        }}
                                                    >
                                                        {formik.values.is_active ? 'Active' : 'Inactive'}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </Box>
                                </Card>
                                {create ?
                                    <Box data-testid="Box-3dge" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', pt: 5 }}>
                                        <Typography data-testid="Typography-0yo0" variant='subtitle2'>
                                            Strips can be rearranged by dragging and dropping
                                        </Typography>
                                    </Box>
                                    : null}
                            </Grid>

                            {/* Second Grid: 10 Cards for each Strip */}

                            <Grid
                                item
                                xs={12} sm={12} md={12}
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    justifyContent: "center",
                                }}
                            >
                                {formik.values.strips.map((strip: SkuStrip, index: number) => (
                                    <Card data-testid="Card-v485" key={index} sx={{ width: { xs: 130, sm: 100, md: 110, lg: 110 }, padding: 1, margin: 0.5, boxShadow: 10 }}>
                                        <Grid container>
                                            <Grid item xs={11}>
                                                <Box data-testid="Box-daau" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Typography data-testid="Typography-3duy" sx={{
                                                        fontWeight: 'bold',
                                                        border: `1px solid black`,
                                                        borderRadius: 2,
                                                        color: 'black',
                                                        paddingX: 1.4,
                                                        ml: 1.1,
                                                        mt: 0.5
                                                    }}>
                                                        {index + 1}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={1}>
                                                {create == true ?
                                                    <Box data-testid="Box-6jxn" sx={{ display: 'flex', alignItems: 'right', justifyContent: 'right' }}>
                                                        {strip.label.trim() !== '' && (
                                                            <Tooltip data-testid="Tooltip-zjhv" title={"Remove Strip"} placement="top">
                                                                <IconButton data-testid="IconButton-miri"
                                                                    onClick={() => {
                                                                        // Clear the strip's label, and if you're using an active state,
                                                                        // remove this index so the card reverts to the plus sign.
                                                                        formik.setFieldValue(`strips[${index}].label`, '');
                                                                        // Optionally, if you're using active state:
                                                                        setActiveStrips((prev: string[]) => prev.filter((i) => i !== strip.dragId));
                                                                    }}

                                                                    sx={{ color: 'red', fontSize: '14px', p: 0, m: 0 }}
                                                                >
                                                                    <CloseIcon sx={{ color: 'red', fontSize: '20px', p: 0, m: 0 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                    : null}
                                            </Grid>
                                        </Grid>
                                    </Card>
                                ))}
                            </Grid>

                            <Grid
                                item
                                xs={12} sm={12} md={12}
                                style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    justifyContent: "center",
                                    paddingTop: 0
                                }}
                            >
                                <DragDropContext onDragEnd={handleDragEnd}>
                                    <Droppable droppableId="strips" direction="horizontal">
                                        {(provided) => (
                                            <Box data-testid="Box-tp3v"
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                                sx={{ display: "flex", gap: 1, justifyContent: "center" }}
                                            >
                                                {formik.values.strips.map((strip: SkuStrip & { dragId: string }, index: number) => {
                                                    // Determine whether we are in "editing" mode if the plus card is clicked,
                                                    // or if a non-empty label is already present.
                                                    const isActive = activeStrips.includes(strip.dragId) || strip.label.trim() !== "";
                                                    return (
                                                        <Draggable key={strip.dragId} draggableId={strip.dragId} index={index} isDragDisabled={create ? false : true}>
                                                            {(provided) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                >
                                                                    <Card data-testid={`Card-qbrz-${index}`} sx={{ width: { xs: 130, sm: 100, md: 110, lg: 110 }, padding: 1, pt: 1, margin: 0.5, boxShadow: 10 }}>
                                                                        {/* Drag handle area */}
                                                                        <Box data-testid={`Box-${index}`}
                                                                            {...provided.dragHandleProps}  // Drag handle is applied here.
                                                                            sx={{
                                                                                display: 'flex',
                                                                                justifyContent: 'center',
                                                                                cursor: 'grab', // Show grab cursor to indicate dragging.
                                                                                mb: 1,
                                                                            }}
                                                                        >
                                                                            {create ? <DragIndicatorIcon data-testid={`Drag-icon-${strip.dragId}`} sx={{ transform: 'rotate(90deg)' }} /> : <LockOutlinedIcon />}
                                                                        </Box>
                                                                        <Divider sx={{ mt: 0.5, mb: 2 }} />
                                                                        {isActive ? (
                                                                            <Box data-testid={`Fields-container-${strip.dragId}`} sx={{ display: 'flex', flexDirection: 'column', gap: 4.5 }}>
                                                                                <Field data-testid={`Field-${strip.dragId}-label`}
                                                                                    testid={`Field-${strip.dragId}`}
                                                                                    name={`strips[${index}].label`}
                                                                                    component={create ? StripSelectEditField : StripSelectReadOnlyEditField}
                                                                                    options={drugCodes}
                                                                                    label="Label"
                                                                                    // Optional: onBlur, if label remains empty, remove from active list:
                                                                                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                                                                                        if (e.target.value.trim() === "") {
                                                                                            setActiveStrips((prev) => prev.filter((i) => i !== strip.dragId));
                                                                                        }
                                                                                    }}
                                                                                />
                                                                                <Field data-testid={`Field-${strip.dragId}-threshold`}
                                                                                    name={`strips[${index}].threshold`}
                                                                                    component={create ? StripTextEditField : StripTextReadOnlyEditField}
                                                                                    label="Threshold"
                                                                                    type="number"
                                                                                />
                                                                                <Field data-testid={`Field-${strip.dragId}-invalid_threshold`}
                                                                                    name={`strips[${index}].invalid_threshold`}
                                                                                    component={create ? TwolineStripTextEditField : TwolineStripReadOnlyTextEditField}
                                                                                    label={<>Invalid<br />Threshold</>}
                                                                                    type="number"
                                                                                />
                                                                            </Box>
                                                                        ) : (
                                                                            // Render a card with a plus icon that, when clicked, marks this index active.
                                                                            <Box data-testid={`Box-${strip.dragId}`}
                                                                                sx={{
                                                                                    width: "100%",
                                                                                    height: "100%",
                                                                                    display: "flex",
                                                                                    justifyContent: "center",
                                                                                    alignItems: "center",
                                                                                }}
                                                                            >
                                                                                {create &&
                                                                                    <IconButton data-testid={`PlusButton-${strip.dragId}`}
                                                                                        onClick={() =>
                                                                                            setActiveStrips((prev) =>
                                                                                                prev.includes(strip.dragId) ? prev : [...prev, strip.dragId]
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        <AddIcon data-testid={`PlusButton-${strip.dragId}`} sx={{ fontSize: 40 }} />
                                                                                    </IconButton>
                                                                                }
                                                                            </Box>
                                                                        )}
                                                                    </Card>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    );
                                                })}
                                                {provided.placeholder}
                                            </Box>
                                        )}
                                    </Droppable>
                                </DragDropContext>

                            </Grid>

                        </Grid>
                    </div>
                    <div data-testid="div-rd25" className="form-group" style={{ width: "100%", display: 'flex', alignItems: 'center', justifyContent: "space-between" }}>
                        <RareButton data-testid="RareButton-b320"
                            onClick={() => handleBackButton()}
                            variant="outlined"
                            sx={{
                                position: "relative",
                                m: 1,
                            }}
                        >
                            Back
                        </RareButton>
                        <RareButton data-testid="RareButton-vj93"
                            type="submit"
                            variant="success"
                            sx={{
                                position: "relative",
                                m: 1,
                            }}
                        >
                            Save Changes
                        </RareButton>
                    </div>
                </Box>
            </form>
        </FormikProvider>
    )
}

export default SkuDetailCard;